import React, { useEffect, useCallback } from "react";
import { useNavigate, useSearchParams, Link as RouterLink } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { Box, Container, Typography, Alert, Link } from "@mui/material";
import BoltRoundedIcon from "@mui/icons-material/BoltRounded";
import EventAvailableRoundedIcon from "@mui/icons-material/EventAvailableRounded";
import PaymentsRoundedIcon from "@mui/icons-material/PaymentsRounded";
import AuthPanel from "../components/AuthPanel";
import { useAuth } from "../context/AuthContext";
import PsychologistApi from "../api/PsychologistApi";
import { pageTitle, lead, h2Rule, paragraph, fadeUp } from "../theme/marketingStyles";

/** /voor-psychologen — registratie + eerlijke uitleg van hoe het werkt voor
 *  psychologen: verificatiestappen, vaste fee per sessie (€5 therapie /
 *  €2 coaching — nooit percentages), maandelijkse afrekening, en waarom we
 *  facturatie-/DAC7-gegevens vragen. Bedragen spiegelen
 *  speakhere-api/src/services/stripe.ts SESSION_FEE_CENTS.
 *  Hero: tekst + punten links, registratiekaart rechts. */
export default function ForPsychologists() {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const { user } = useAuth();
  const [params] = useSearchParams();
  const handoff = params.get("handoff");

  // After auth (or if already logged in): apply the vind-hulp handoff prefill, then go to the dashboard.
  const finish = useCallback(async () => {
    if (handoff) { try { await PsychologistApi.handoff(handoff); } catch { /* ignore */ } }
    navigate("/dashboard");
  }, [handoff, navigate]);

  useEffect(() => { if (user && handoff) finish(); }, [user, handoff, finish]);

  const points = [
    { icon: <BoltRoundedIcon />, text: t("forPsy.point1") },
    { icon: <EventAvailableRoundedIcon />, text: t("forPsy.point2") },
    { icon: <PaymentsRoundedIcon />, text: t("forPsy.point3") },
  ];

  return (
    <Box sx={{ bgcolor: "background.default" }}>
      <Container maxWidth="lg" sx={{ pt: { xs: 6, sm: 9 }, pb: { xs: 7, sm: 10 } }}>
        <Box sx={{ display: "grid", gridTemplateColumns: { xs: "1fr", md: "7fr 5fr" }, gap: { xs: 5, md: 8 }, alignItems: "start", mb: { xs: 2, sm: 4 } }}>
          <Box sx={{ textAlign: { xs: "center", md: "left" }, ...fadeUp(0) }}>
            <Typography variant="h1" sx={pageTitle}>{t("forPsy.title")}</Typography>
            <Typography sx={{ ...lead, mb: 4, maxWidth: 620, mx: { xs: "auto", md: 0 } }}>{t("forPsy.blurb")}</Typography>
            <Box sx={{ display: "grid", gap: 2, justifyItems: { xs: "center", md: "start" } }}>
              {points.map((pt, i) => (
                <Box key={i} sx={{ display: "flex", gap: 2, alignItems: "center", textAlign: "left" }}>
                  <Box sx={{ color: "var(--accent-700)", "& svg": { fontSize: 26 } }}>{pt.icon}</Box>
                  <Typography sx={{ m: 0 }}>{pt.text}</Typography>
                </Box>
              ))}
            </Box>
          </Box>

          {/* Registratiekaart — AuthPanel brengt zijn eigen zachte kaart mee. */}
          <Box sx={fadeUp(120)}>
            {handoff && <Alert severity="info" sx={{ mb: 2 }}>{t("forPsy.handoff")}</Alert>}
            {user ? (
              <Typography sx={{ m: 0 }}>
                {t("forPsy.loggedIn")}{" "}
                <Box component="span" sx={{ color: "primary.main", cursor: "pointer", fontWeight: 600 }} onClick={() => navigate("/dashboard")}>
                  {t("dashboard.nav")}
                </Box>
              </Typography>
            ) : (
              <AuthPanel initialMode="register" role="psychologist" onSuccess={finish} />
            )}
          </Box>
        </Box>

        <Container maxWidth="md" disableGutters sx={{ mx: 0 }}>
          <Typography variant="h2" sx={h2Rule}>{t("forPsy.vettingTitle")}</Typography>
          <Typography sx={paragraph}>{t("forPsy.vettingIntro")}</Typography>
          <Box component="ul" sx={{ mt: 0, mb: 2, pl: 3, "& li::marker": { color: "var(--primary-300)" } }}>
            {["forPsy.vetting1", "forPsy.vetting2", "forPsy.vetting3", "forPsy.vetting4"].map((k) => (
              <Typography key={k} component="li" sx={{ mb: 1, color: "text.secondary", lineHeight: 1.7 }}>{t(k)}</Typography>
            ))}
          </Box>
          <Typography sx={paragraph}>
            {t("forPsy.vettingOutro")}{" "}
            <Link component={RouterLink} to="/kwaliteit-en-erkenning">{t("forPsy.vettingLink")}</Link>
          </Typography>

          <Typography variant="h2" sx={h2Rule}>{t("forPsy.feeTitle")}</Typography>
          <Typography sx={paragraph}>{t("forPsy.fee1")}</Typography>
          <Box component="ul" sx={{ mt: 0, mb: 2, pl: 3, "& li::marker": { color: "var(--primary-300)" } }}>
            {["forPsy.feeB1", "forPsy.feeB2", "forPsy.feeB3"].map((k) => (
              <Typography key={k} component="li" sx={{ mb: 1, color: "text.secondary", lineHeight: 1.7 }}>{t(k)}</Typography>
            ))}
          </Box>
          <Typography sx={paragraph}>{t("forPsy.fee2")}</Typography>

          <Typography variant="h2" sx={h2Rule}>{t("forPsy.payoutTitle")}</Typography>
          <Typography sx={paragraph}>{t("forPsy.payout1")}</Typography>
          <Typography sx={paragraph}>{t("forPsy.payout2")}</Typography>

          <Typography variant="h2" sx={h2Rule}>{t("forPsy.dac7Title")}</Typography>
          <Typography sx={paragraph}>{t("forPsy.dac71")}</Typography>
          <Typography sx={paragraph}>{t("forPsy.dac72")}</Typography>

          {/* Tools voor tussen de sessies: (verdiepende) trajecten en
              reflectie-vragenlijsten toewijzen. */}
          <Typography variant="h2" sx={h2Rule}>{t("forPsy.assignTitle")}</Typography>
          <Typography sx={paragraph}>{t("forPsy.assign1")}</Typography>
          <Typography sx={paragraph}>{t("forPsy.assign2")}</Typography>

          <Typography variant="h2" sx={h2Rule}>{t("forPsy.vindhulpTitle")}</Typography>
          {/* "vind-hulp.be" klikbaar maken: de tekst bevat in alle talen de
              letterlijke domeinnaam, dus we splitsen erop en voegen de link in. */}
          <Typography sx={paragraph}>
            {t("forPsy.vindhulp1").split("vind-hulp.be").map((part, i, arr) => (
              <React.Fragment key={i}>
                {part}
                {i < arr.length - 1 && (
                  <Link href="https://www.vind-hulp.be" target="_blank" rel="noopener noreferrer">vind-hulp.be</Link>
                )}
              </React.Fragment>
            ))}
          </Typography>
        </Container>
      </Container>
    </Box>
  );
}
